<script setup>
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { useProjectsStore } from '@/stores/projects'
import ProjectLayout from '@/layouts/ProjectLayout.vue'

const route = useRoute()
const projectId = route.params.id
const projectsStore = useProjectsStore()
const { project, pending, error } = storeToRefs(projectsStore)

onMounted(() => {
  projectsStore.getProjectId(projectId)
})
</script>

<template>
  <ProjectLayout>
    <!-- Error State -->
    <div v-if="error" class="p-4 text-red-500">
      {{ error }}
    </div>

    <!-- Loading State -->
    <div v-if="pending" class="flex flex-col md:flex-row h-screen animate-pulse">
      <!-- Left Column Skeleton -->
      <div class="w-full md:w-2/3 p-4 space-y-4">
        <div class="h-8 bg-gray-300 rounded w-1/2"></div>
        <div class="h-4 bg-gray-200 rounded w-3/4"></div>
        <div class="h-64 bg-gray-300 rounded"></div>
        <div class="space-y-2">
          <div class="h-4 bg-gray-200 rounded w-full"></div>
          <div class="grid grid-cols-2 gap-4">
            <div class="h-32 bg-gray-300 rounded"></div>
            <div class="h-32 bg-gray-300 rounded"></div>
          </div>
        </div>
      </div>

      <!-- Right Column Skeleton -->
      <div class="w-full md:w-1/3 p-4 space-y-4">
        <div class="h-6 bg-gray-300 rounded w-1/3"></div>
        <div class="space-y-2">
          <div class="h-4 bg-gray-200 rounded w-full"></div>
          <div class="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
        <div class="space-y-2">
          <div class="h-4 bg-gray-200 rounded w-full"></div>
          <div class="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    </div>

    <!-- Success State -->
    <div v-if="project" class="flex flex-col md:flex-row h-screen">
      <!-- Project Content -->
      <div class="w-full md:w-2/3 p-4 overflow-y-auto">
        <h1 class="text-3xl font-bold text-[#776849] mb-4">
          {{ project.title }}
        </h1>
        <p class="text-gray-600 mb-6">{{ project.description }}</p>

        <div class="mb-8">
          <NuxtImg
            :src="project.image"
            :placeholder="50"
            class="w-full rounded-lg shadow-md"
            alt="Project preview"
          />
        </div>

        <div v-if="project.screenshots" class="mb-6">
          <h3 class="text-xl font-semibold text-gray-800 mb-4">Screenshots</h3>
          <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
            <NuxtImg
              v-for="(screenshot, index) in project.screenshots"
              :key="index"
              :src="screenshot"
              :placeholder="50"
              class="w-full h-48 object-cover rounded-md shadow-sm"
              alt="Project screenshot"
            />
          </div>
        </div>
      </div>

      <!-- Project Details -->
      <div class="w-full md:w-1/3 p-4 bg-gray-50 border-l">
        <h2 class="text-2xl font-bold text-[#776849] mb-6">Details</h2>

        <div class="space-y-4">
          <div>
            <h3 class="text-lg font-semibold text-gray-700">Use Case</h3>
            <p class="text-gray-600">{{ project.useCase }}</p>
          </div>

          <div>
            <h3 class="text-lg font-semibold text-gray-700">Problem Solved</h3>
            <p class="text-gray-600">{{ project.problem }}</p>
          </div>

          <div>
            <h3 class="text-lg font-semibold text-gray-700">Solution</h3>
            <p class="text-gray-600">{{ project.solution }}</p>
          </div>

          <div>
            <h3 class="text-lg font-semibold text-gray-700">Impact</h3>
            <p class="text-gray-600">{{ project.impact }}</p>
          </div>

          <div>
            <h3 class="text-lg font-semibold text-gray-700">Links</h3>
            <ul class="text-blue-600 space-y-2">
              <li>
                <a :href="project.demoLink" target="_blank" class="hover:underline">
                  Live Demo
                </a>
              </li>
              <li>
                <a :href="project.repoLink" target="_blank" class="hover:underline">
                  Source Code
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </ProjectLayout>
</template>

<style scoped>
.animate-pulse {
  animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

@media (max-width: 768px) {
  .grid-cols-2 {
    grid-template-columns: 1fr;
  }
  
  .h-48 {
    height: 200px;
  }
}
</style>
